"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { uploadCardPhoto } from "src/components/greeting-card/editor/upload-photo";
import {
  buildPartyInvitationTemplate,
  formatPartyInvitationDate,
  PARTY_INVITATION_CODE,
} from "src/components/greeting-card/templates/party-invitation";
import type { CardTemplate } from "src/components/greeting-card/templates/types";
import { hostPartyApi } from "./host-api";

/** Width of the QR the invitation prints: 1200 px over ~64 mm is ~475 dpi. */
export const INVITATION_QR_PX = 1200;

interface PartyInvitationData {
  name: string | null;
  startsAt: string | null;
  qr: { sourceKey: string; aspect: number } | null;
}

/** Height / width of an image; 1 (square) when the browser cannot tell. */
async function imageAspect(blob: Blob): Promise<number> {
  try {
    if (typeof createImageBitmap !== "function") return 1;
    const bmp = await createImageBitmap(blob);
    const aspect = bmp.width > 0 ? bmp.height / bmp.width : 1;
    bmp.close?.();
    return aspect;
  } catch {
    return 1;
  }
}

/** The event's styled QR, uploaded exactly as a customer photo is, so the editor and the print render load it. */
async function uploadPartyQr(blob: Blob, name: string): Promise<{ sourceKey: string; aspect: number }> {
  const file = new File([blob], `dogodek-qr-${name}.png`, { type: "image/png" });
  const [sourceKey, aspect] = await Promise.all([uploadCardPhoto(file), imageAspect(blob)]);
  return { sourceKey, aspect };
}

/** Never rejects: a missing name or QR leaves that part of the invitation at its default. */
async function loadPartyInvitationData(partyId: string): Promise<PartyInvitationData> {
  const [party, qr] = await Promise.allSettled([
    hostPartyApi.stats(partyId),
    hostPartyApi.qrPng(partyId, INVITATION_QR_PX).then((blob) => uploadPartyQr(blob, partyId)),
  ]);
  return {
    name: party.status === "fulfilled" ? party.value.name : null,
    startsAt: party.status === "fulfilled" ? party.value.startsAt : null,
    qr: qr.status === "fulfilled" ? qr.value : null,
  };
}

/** The same, from the app's invitation token (no login in its webview). Never rejects. */
async function loadInvitationDataFromToken(token: string): Promise<PartyInvitationData> {
  let assets: { name: string; startsAt: string; qrPngUrl: string };
  try {
    assets = await hostPartyApi.invitationAssets(token);
  } catch {
    return { name: null, startsAt: null, qr: null };
  }
  let qr: PartyInvitationData["qr"] = null;
  try {
    const res = await fetch(assets.qrPngUrl);
    if (!res.ok) throw new Error(`qr ${res.status}`);
    qr = await uploadPartyQr(await res.blob(), "vabilo");
  } catch {
    qr = null;
  }
  return { name: assets.name ?? null, startsAt: assets.startsAt ?? null, qr };
}

/**
 * The party invitation template for the card editor, when the page asks for it:
 * `?party=<id>` (from the printed-set screen, host logged in), `?inviteToken=`
 * (from the app, whose webview has no login) or `?template=party-invitation`.
 *
 * With a party it waits for the event's name, date and QR (`pending`), so the
 * template is applied once, already filled. A QR that cannot be fetched leaves
 * the slot empty and sets `qrUnavailable`; the editor opens regardless.
 */
export function usePartyInvitationTemplate({
  kind,
  partyId,
  inviteToken = null,
  templateCode,
  locale,
}: {
  kind: string | undefined;
  partyId: string | null;
  inviteToken?: string | null;
  templateCode: string | null;
  locale: string;
}): { template: CardTemplate | null; pending: boolean; qrUnavailable: boolean } {
  const wanted =
    kind === "invitation" &&
    (!!partyId || !!inviteToken || templateCode?.trim().toLowerCase() === PARTY_INVITATION_CODE);
  const forParty = wanted && (!!partyId || !!inviteToken);

  const query = useQuery({
    queryKey: ["party-invitation", inviteToken ? `token:${inviteToken}` : partyId],
    queryFn: () => (inviteToken ? loadInvitationDataFromToken(inviteToken) : loadPartyInvitationData(partyId!)),
    enabled: forParty,
    // Each run uploads a new copy of the QR: once per page visit is enough.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const data = query.data;
  const template = useMemo(() => {
    if (!wanted) return null;
    if (!forParty) return buildPartyInvitationTemplate();
    if (!data) return null;
    return buildPartyInvitationTemplate({
      name: data.name ?? undefined,
      date: data.startsAt ? formatPartyInvitationDate(data.startsAt, locale) : undefined,
      qr: data.qr ?? undefined,
    });
  }, [wanted, forParty, data, locale]);

  return {
    template,
    pending: forParty && !data,
    qrUnavailable: forParty && !!data && !data.qr,
  };
}
